import React, { memo } from 'react';
import { useLocation } from 'react-router-dom';
import { Accordion as SatoAccordion } from '@sato/components';

import { Link } from '@components/common/Link';

import { ResolveEntry } from '../components';
import { EntryType, ICollapsableContentFields, ParsedEntry } from '../types';
import { includesEntry } from '../utils/entry.utils';

interface Props extends Omit<ICollapsableContentFields, 'content'> {
  id?: string;
  children: ParsedEntry<EntryType>[] | string;
}

export const Accordion = memo(
  ({
    id,
    header,
    children,
    includeGap = true,
    isOpenByDefault = false,
    icon,
    ...props
  }: Props) => {
    const { hash } = useLocation();

    const focusedEntryId = hash.slice(1);
    const shouldOpen =
      id === focusedEntryId ||
      (typeof children !== 'string' && includesEntry(children, focusedEntryId));

    return (
      <SatoAccordion
        {...props}
        title={header}
        id={id}
        defaultExpanded={shouldOpen || isOpenByDefault}
        className="py-3"
        onExpandedChange={(isExpanded) => {
          if (!(isExpanded && shouldOpen)) return;
          // scroll to content only after it is ready to be displayed (e.g. the accordion is opened)
          requestAnimationFrame(() =>
            window.document.getElementById(focusedEntryId)?.scrollIntoView(true)
          );
        }}
        icon={icon?.icon}
        textBlockProps={{ linkComponent: Link }}
        includeGaps={includeGap}
      >
        {typeof children === 'string'
          ? // legacy support for old content
            children
          : children?.map((entry) => (
              <ResolveEntry key={entry.id} entry={entry} />
            ))}
      </SatoAccordion>
    );
  }
);
